import { useEffect, useState, useMemo } from "react";
import { photoReduite } from "@/lib/utils/photo";
import { photoEmballage } from "@/lib/utils/emballages";
import { nomArticle } from "@/lib/utils/noms-ar";
import { api } from "@/lib/firebase/api-client";
import { intervalleVisible } from "@/lib/utils/intervalle-visible";
import { Order } from "@/types";
import { OrderStatus } from "@/lib/constants";
import { Package, CheckCircle, XCircle, Eye, AlertCircle, Clock, Users, ShoppingBag, Timer, Printer } from "lucide-react";
import { useLangue } from "@/lib/i18n";
import Loading from "@/components/shared/Loading";
import PresenceFournisseurs from "@/components/PresenceFournisseurs";
import PrixVolailleCaisse from "@/components/PrixVolailleCaisse";
import { useAuth } from "@/lib/hooks/useAuth";
import { useOrders as useOrdersQuery } from "@/lib/hooks/queries";
import { useQueryClient } from "@tanstack/react-query";
import { getDocuments } from "@/lib/firebase/firestore";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import { db } from "@/lib/firebase/config";
import { COLLECTIONS } from "@/lib/constants";
import { handleError, handleSuccess } from "@/lib/errors/error-handler";
import { formatDate } from "@/lib/utils/format";
import { imprimerTicket, surAndroid } from "@/lib/utils/ticket-commande";

/**
 * LE NUMERO DE COMMANDE EN ENTIER, a cote du code de retrait (20/10/2026).
 * Le responsable commande imprime les tickets : il lui faut le numero
 * Glovo complet (101808…), pas seulement le code a 3 chiffres.
 */
function NumeroCommande({ commande }: { commande: any }) {
  const numero = String(commande?.orderCode || commande?.externalId || "");
  if (!numero) return null;
  return (
    <span
      dir="ltr"
      className="select-all rounded-lg border border-gray-300 bg-white px-2.5 py-1 font-mono text-base md:text-lg font-black tracking-wide text-gray-900"
      title="Numéro de commande"
    >
      N° {numero}
    </span>
  );
}

const STATUS_COLORS: Record<OrderStatus, string> = {
  PENDING: "bg-yellow-100 text-yellow-800",
  GLOVO_ACCEPTED: "bg-blue-100 text-blue-800",
  IN_PREPARATION: "bg-purple-100 text-purple-800",
  GLOVO_READY: "bg-green-100 text-green-800",
  GLOVO_DELIVERED: "bg-blue-100 text-blue-800",
  CANCELLED: "bg-red-100 text-red-800",
  GLOVO_CANCELLED: "bg-red-100 text-red-800",
};

const STATUS_LABELS: Record<OrderStatus, string> = {
  PENDING: "En attente",
  GLOVO_ACCEPTED: "Acceptée",
  IN_PREPARATION: "En préparation",
  GLOVO_READY: "Prête",
  GLOVO_DELIVERED: "Livrée",
  CANCELLED: "Annulée",
  GLOVO_CANCELLED: "Annulée (Glovo)",
};

const BASKET_STATUS_LABELS: Record<string, string> = {
  PENDING: "En attente",
  PREPARING: "En préparation",
  READY: "Prêt",
  PICKED_UP: "Récupéré",
};

export default function CollaborateurDashboard() {
  // Les libelles d'etat sont traduits a l'affichage, pas dans les
  // constantes : celles-ci sont lues au chargement du module, avant meme
  // qu'on sache dans quelle langue on travaille.
  const { t, langue } = useLangue();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);
  // RETRAIT D'UN ARTICLE INDISPONIBLE (21/10/2026). L'article dont on
  // confirme la demande, et celui dont la demande part. Pas de fenetre du
  // navigateur : la confirmation s'affiche sur la ligne de l'article.
  const [retraitAConfirmer, setRetraitAConfirmer] = useState<string | null>(null);
  const [retraitEnvoi, setRetraitEnvoi] = useState<string | null>(null);
  // PAYER PAR LA CAISSE (21/10/2026) : l'article dont on saisit le total,
  // le montant tape, et l'envoi en cours.
  const [caisseOuverte, setCaisseOuverte] = useState<string | null>(null);
  const [caisseTotal, setCaisseTotal] = useState("");
  const [caisseEnvoi, setCaisseEnvoi] = useState<string | null>(null);
  const [userStoreId, setUserStoreId] = useState<string | null | "loading">("loading");

  // ===================================================================
  //  L'ECOUTE DES PANIERS PRETS
  // ===================================================================
  //
  //  Avant, cet ecran redemandait la liste toutes les 30 secondes. Le
  //  fournisseur appuyait sur « Prêt » et le responsable l'apprenait
  //  jusqu'a une demi-minute plus tard — le livreur attendait pendant ce
  //  temps-la.
  //
  //  Ici on ECOUTE. Des qu'un panier passe « Prêt », la liste se rafraichit.
  //
  //  01/11/2026 — DEUX GARDE-FOUS, POUR LE COUT :
  //    - on n'ecoute plus que les paniers DE SON MAGASIN (avant : tous les
  //      magasins, et chaque panier pret d'Oulfa rechargeait la liste de
  //      Bourgogne) ;
  //    - au plus UN rechargement toutes les 30 secondes : le premier panier
  //      pret arrive tout de suite, les suivants de la meme demi-minute sont
  //      regroupes en un seul rechargement.
  //  Deux egalites (statut + magasin) : Firestore les sert sans index
  //  composite.
  //
  //  Le sondage par le compteur n'est PAS supprime : il reste le filet si
  //  cette ecoute tombe (reseau coupe, onglet en veille, droits refuses).
  useEffect(() => {
    if (userStoreId === "loading") return;
    let premierEnvoi = true;
    let dernier = 0;
    let minuterie: number | undefined;
    const recharger = () => {
      dernier = Date.now();
      minuterie = undefined;
      // On ne recharge pas la liste a la main : on la marque perimee, et
      // react-query la redemande. Un seul chemin de chargement.
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
    };

    const conditions = [where("status", "==", "READY")];
    if (userStoreId) conditions.push(where("storeId", "==", userStoreId));

    const ecoute = onSnapshot(
      query(collection(db, COLLECTIONS.SUPPLIER_BASKETS), ...conditions),
      (snapshot) => {
        // Firestore livre tout le contenu initial comme des « ajouts ».
        if (premierEnvoi) {
          premierEnvoi = false;
          return;
        }
        // Seul un panier qui DEVIENT pret compte. Un panier qui sort de
        // « Prêt » (recupere) est deja suivi par l'action qui l'a sorti.
        if (!snapshot.docChanges().some((c) => c.type === "added")) return;
        const attente = 30 * 1000 - (Date.now() - dernier);
        if (attente <= 0) recharger();
        else if (minuterie === undefined) minuterie = window.setTimeout(recharger, attente);
      },
      (err) => {
        // Une ecoute qui tombe ne doit jamais casser l'ecran : le compteur
        // continue de faire son travail.
        console.error("[PANIERS PRETS] Ecoute interrompue :", err?.message);
      },
    );

    return () => {
      ecoute();
      if (minuterie !== undefined) window.clearTimeout(minuterie);
    };
  }, [queryClient, userStoreId]);
  const [activeTab, setActiveTab] = useState<"pending" | "inPreparation" | "ready">("pending");
  // La commande dont on est en train de compter les sacs, et les deux
  // compteurs. `null` = aucune fenetre ouverte.
  const [sacsPour, setSacsPour] = useState<Order | null>(null);
  const [sacKraft, setSacKraft] = useState(0);
  const [sacTissu, setSacTissu] = useState(0);

  // Les achats a faire a la caisse, parce qu'un fournisseur n'avait pas
  // l'article. Ils passent AVANT les commandes : tant qu'un achat n'est pas
  // fait, la commande ne peut pas partir.
  const [achats, setAchats] = useState<any[]>([]);
  const [prixPayes, setPrixPayes] = useState<Record<string, string>>({});
  // Volaille achetee en caisse (18/10/2026) : le poids pese, et le prix du
  // kilo quand il n'a pas ete donne ce matin.
  const [poidsAchats, setPoidsAchats] = useState<Record<string, string>>({});
  const [prixKgAchats, setPrixKgAchats] = useState<Record<string, string>>({});
  const [achatEnCours, setAchatEnCours] = useState<string | null>(null);

  async function chargerAchats() {
    try {
      const r = await api.orders.getAchatsCaisse("DEMANDE");
      setAchats(r.lignes);
    } catch {
      /* La liste est un rappel : son absence ne bloque pas les commandes. */
    }
  }

  useEffect(() => {
    void chargerAchats();
    // Un achat peut tomber a tout moment : on relit regulierement plutot que
    // de compter sur un rechargement de page qui n'arrivera jamais de la
    // journee.
    //
    // Onglet cache : plus d'appel au serveur ; retour : rafraichi aussitot.
    // (Ce n'est pas une alarme : l'alarme des commandes est a part.)
    return intervalleVisible(() => void chargerAchats(), 60000);
  }, []);

  /** Le fournisseur s'est trompe : l'article repart chez lui, rien en caisse. */
  async function annulerErreur(depense: any) {
    if (
      !window.confirm(
        `Le fournisseur a « ${depense.article} » ?\n\nL'achat en caisse est annulé et l'article redevient à préparer chez lui.`,
      )
    )
      return;
    setAchatEnCours(depense.id);
    try {
      await api.orders.soldierAchatCaisse(depense.id, { erreurFournisseur: true });
      await chargerAchats();
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    } catch (e: any) {
      alert(`Impossible d'annuler : ${e.message}`);
    } finally {
      setAchatEnCours(null);
    }
  }

  async function solder(depense: any, introuvable: boolean) {
    setAchatEnCours(depense.id);
    try {
      if (introuvable) {
        if (!window.confirm(`Marquer « ${depense.article} » introuvable ?`)) {
          setAchatEnCours(null);
          return;
        }
        await api.orders.soldierAchatCaisse(depense.id, { introuvable: true });
      } else if (depense.auPoids) {
        const poids = Number((poidsAchats[depense.id] || "").replace(",", "."));
        if (!(poids > 0)) {
          alert("Pèse le poulet et entre son poids (en kg).");
          setAchatEnCours(null);
          return;
        }
        const prixKg = Number((prixKgAchats[depense.id] || "").replace(",", "."));
        if (!depense.prixKgConnu && !(prixKg > 0)) {
          alert("Entre le prix du kilo payé aujourd'hui.");
          setAchatEnCours(null);
          return;
        }
        await api.orders.soldierAchatCaisse(depense.id, {
          poidsKg: poids,
          ...(depense.prixKgConnu ? {} : { prixKg }),
        });
      } else {
        const saisi = (prixPayes[depense.id] || "").replace(",", ".");
        const dirhams = Number(saisi);
        if (!(dirhams > 0)) {
          alert("Saisis le prix réellement payé.");
          setAchatEnCours(null);
          return;
        }
        const r = await api.orders.soldierAchatCaisse(depense.id, {
          prixPayeCentimes: Math.round(dirhams * 100),
        });
        if (r.depassement) {
          alert(
            "Enregistré — mais au-dessus du maximum autorisé. " +
              "Le dépassement est signalé au responsable.",
          );
        }
      }
      await chargerAchats();
    } catch (e: any) {
      alert(`Impossible d'enregistrer : ${e.message}`);
    } finally {
      setAchatEnCours(null);
    }
  }

  // StoreId: priorité à user.storeId (déjà chargé par auth), sinon fallback getDocuments
  useEffect(() => {
    if (!user || user.role !== "COLLABORATEUR") {
      if (user && user.role !== "COLLABORATEUR") setUserStoreId(null);
      return;
    }
    if (user.storeId) {
      setUserStoreId(user.storeId);
      return;
    }
    async function fetchStore() {
      try {
        let storeId: string | null = null;
        const collabs = await getDocuments(COLLECTIONS.COLLABORATEURS, [
          { field: "userId", operator: "==", value: user!.uid },
        ], undefined, "asc", 1);
        storeId = collabs[0]?.storeId ?? null;
        if (!storeId) {
          const collabDoc = await getDoc(doc(db, COLLECTIONS.COLLABORATEURS, user!.uid));
          storeId = collabDoc.exists() ? (collabDoc.data()?.storeId ?? null) : null;
        }
        setUserStoreId(storeId);
      } catch {
        setUserStoreId(null);
      }
    }
    fetchStore();
  }, [user?.uid, user?.role, user?.storeId]);

  // ✅ React Query pour orders
  const ordersFilters = useMemo(() => {
    if (userStoreId === "loading" || !userStoreId) return undefined;
    return { storeId: userStoreId };
  }, [userStoreId]);
  
  const { data: ordersResponse, isLoading: loading } = useOrdersQuery(ordersFilters);
  
  const rawOrders = useMemo(() => {
    if (!ordersResponse) return [];
    return (ordersResponse as any).orders || [];
  }, [ordersResponse]);

  // Trier: PENDING en premier, puis par date
  const orders = useMemo(() => {
    return [...rawOrders].sort((a: any, b: any) => {
      if (a.status === OrderStatus.PENDING && b.status !== OrderStatus.PENDING) return -1;
      if (a.status !== OrderStatus.PENDING && b.status === OrderStatus.PENDING) return 1;
      const ca = a.createdAt as any;
      const cb = b.createdAt as any;
      const dateA = ca?.toDate ? ca.toDate() : new Date(ca);
      const dateB = cb?.toDate ? cb.toDate() : new Date(cb);
      return dateB.getTime() - dateA.getTime();
    });
  }, [rawOrders]);

  async function handleAccept(orderId: string) {
    try {
      setProcessing(orderId);
      await api.orders.accept(orderId);
      // ✅ Invalider le cache
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      handleSuccess(t("Commande acceptée avec succès !"));
      if (selectedOrder?.id === orderId) setSelectedOrder(null);
    } catch (error: any) {
      handleError(error, "Accepter commande");
      // L'ECRAN ETAIT EN RETARD SUR LA REALITE : le plus souvent, quelqu'un
      // d'autre a deja accepte. On recharge la liste et on ferme la fiche,
      // pour que le bouton « Accepter » ne reste pas sous les doigts d'une
      // commande qui n'attend plus rien.
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      if (selectedOrder?.id === orderId) setSelectedOrder(null);
    } finally {
      setProcessing(null);
    }
  }

  // TANT QU'UN RETRAIT ATTEND LE PATRON, la fiche ouverte se relit toutes les
  // 15 secondes : le responsable voit la decision sans fermer ni rouvrir.
  const retraitEnAttente = ((selectedOrder as any)?.items || []).some(
    (i: any) => i?.retrait?.statut === "EN_ATTENTE" || i?.retrait?.statut === "EN_COURS",
  );
  useEffect(() => {
    if (!selectedOrder?.id || !retraitEnAttente) return;
    const id = selectedOrder.id;
    const minuteur = setInterval(async () => {
      try {
        const updated = await api.orders.getById(id);
        setSelectedOrder((actuelle) => (actuelle?.id === id ? updated : actuelle));
      } catch {
        /* reseau coupe : on reessaiera au prochain tour */
      }
    }, 15000);
    return () => clearInterval(minuteur);
  }, [selectedOrder?.id, retraitEnAttente]);

  /**
   * LE TICKET A ACCROCHER AU SAC (25/10/2026). Il part vers l'appli RawBT,
   * qui l'imprime sur l'imprimante du magasin. Le magasin se lit sur la
   * commande ; le nom du responsable sur sa session.
   */
  const MAGASINS_TICKET: Record<string, string> = {
    "2EqQqqb3Amrhm3h0P0tS": "Oulfa - Glovo",
    e8Mn6vPL4euqB4Vt68Ym: "Bourgogne - Glovo",
  };
  function handleImprimerTicket(commande: any) {
    if (!surAndroid()) {
      handleError(
        new Error("Le ticket s'imprime depuis un téléphone Android, avec l'appli RawBT."),
        "Imprimer le ticket",
      );
      return;
    }
    const ok = imprimerTicket(commande, {
      magasin: MAGASINS_TICKET[String(commande?.storeId || "")] || "",
      preparePar: (user as any)?.name || (user as any)?.username || "",
    });
    if (!ok) handleError(new Error("Impression impossible sur cet appareil."), "Imprimer le ticket");
  }

  async function handleMarkReady(orderId: string) {
    try {
      setProcessing(orderId);
      await api.orders.markReady(orderId);
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      handleSuccess(t("Commande marquée comme prête !"));
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(null);
      }
    } catch (error: any) {
      handleError(error, "Marquer prête");
    } finally {
      setProcessing(null);
    }
  }

  /**
   * Le responsable ne trouve pas l'article, ni chez le fournisseur ni en
   * caisse : il le RETIRE (02/11/2026, decision du patron : plus de
   * validation). L'article part en ventes perdues.
   */
  async function demanderRetrait(orderItemId: string) {
    if (!selectedOrder) return;
    try {
      setRetraitEnvoi(orderItemId);
      // 02/11/2026 : retrait DIRECT, sans validation du patron. L'article
      // part aussitot en ventes perdues, au nom de celui qui l'a retire.
      const r = await api.retraitsCommande.retirer(orderItemId);
      handleSuccess(t("Article retiré — ventes perdues") + (r.raison ? ` — ${r.raison}` : ""));
      const updated = await api.orders.getById(selectedOrder.id);
      setSelectedOrder(updated);
    } catch (error: any) {
      handleError(error, "Retrait d'article");
    } finally {
      setRetraitEnvoi(null);
      setRetraitAConfirmer(null);
    }
  }

  /**
   * Le responsable a achete l'article lui-meme : il donne le TOTAL paye pour
   * la ligne, le serveur divise par la quantite et sort l'achat de SA caisse.
   */
  async function payerParCaisse(item: any) {
    if (!selectedOrder) return;
    const total = Number(String(caisseTotal).replace(",", "."));
    if (!Number.isFinite(total) || total <= 0) {
      handleError(new Error(t("Écris le total payé")), "Payer par la caisse");
      return;
    }
    try {
      setCaisseEnvoi(item.id);
      await api.orders.payerCaisse(selectedOrder.id, item.id, total);
      handleSuccess(t("Payé par la caisse"));
      setCaisseOuverte(null);
      setCaisseTotal("");
      const updated = await api.orders.getById(selectedOrder.id);
      setSelectedOrder(updated);
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    } catch (error: any) {
      handleError(error, "Payer par la caisse");
    } finally {
      setCaisseEnvoi(null);
    }
  }

  async function handleBasketPickup(basketId: string) {
    try {
      setProcessing(basketId);
      await api.baskets.pickup(basketId);
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["baskets"] });
      if (selectedOrder) {
        const updated = await api.orders.getById(selectedOrder.id);
        setSelectedOrder(updated);
      }
      handleSuccess(t("Panier récupéré !"));
    } catch (error: any) {
      handleError(error, "Récupérer panier");
    } finally {
      setProcessing(null);
    }
  }

  // Fournisseur debord e (poisson, pain…) : le responsable valide le panier
  // a sa place — pret puis recupere en un geste. Trace cote serveur.
  async function handleValiderPourFournisseur(basketId: string) {
    if (!confirm(t("Valider ce panier à la place du fournisseur ?"))) return;
    try {
      setProcessing(basketId);
      await api.baskets.markReady(basketId);
      await api.baskets.pickup(basketId);
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["baskets"] });
      if (selectedOrder) {
        const updated = await api.orders.getById(selectedOrder.id);
        setSelectedOrder(updated);
      }
      handleSuccess(t("Panier validé et récupéré"));
    } catch (error: any) {
      handleError(error, "Valider le panier");
    } finally {
      setProcessing(null);
    }
  }

  async function handleCancel(orderId: string) {
    if (!confirm(t("Êtes-vous sûr de vouloir annuler cette commande ?"))) return;
    try {
      setProcessing(orderId);
      await api.orders.cancel(orderId);
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      handleSuccess(t("Commande annulée"));
      if (selectedOrder?.id === orderId) setSelectedOrder(null);
    } catch (error: any) {
      handleError(error, "Annuler commande");
    } finally {
      setProcessing(null);
    }
  }

  /**
   * Marquer livree ne valide plus directement : ca ouvre le comptage.
   *
   * Le nom de la fonction ne change pas, et c'est voulu : DEUX boutons
   * l'appellent — celui de la carte dans la liste, et celui du detail. En
   * modifiant ici, les deux passent par le comptage. Si on n'avait traite
   * que la fenetre de detail, le bouton rapide de la liste serait reste un
   * raccourci pour ne rien saisir, et c'est par la que tout serait passe.
   */
  function handleMarkDelivered(orderId: string) {
    const cmd =
      (orders as any[]).find((o: any) => o.id === orderId) ||
      (selectedOrder?.id === orderId ? selectedOrder : null);
    if (!cmd) return;
    setSacKraft(0);
    setSacTissu(0);
    setSacsPour(cmd as Order);
  }

  /** La vraie validation, une fois les sacs comptes. */
  async function confirmerLivraison() {
    const cmd = sacsPour;
    if (!cmd) return;
    // Filet d'ecran ; le serveur refuse de toute facon un total nul.
    if (sacKraft + sacTissu === 0) return;

    try {
      setProcessing(cmd.id);
      await api.orders.markDelivered(cmd.id, { sacKraft, sacTissu });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      handleSuccess(t("Commande marquée comme livrée !"));
      setSacsPour(null);
      if (selectedOrder?.id === cmd.id) setSelectedOrder(null);
    } catch (error: any) {
      handleError(error, "Marquer livrée");
    } finally {
      setProcessing(null);
    }
  }

  async function handleOpenDetails(order: Order) {
    try {
      const fullOrder = await api.orders.getById(order.id);
      setSelectedOrder(fullOrder);
    } catch (error) {
      console.error("Error loading order details:", error);
      setSelectedOrder(order);
    }
  }

  // =================================================================
  //  DEUX RESPONSABLES DANS LE MEME MAGASIN (15/10/2026)
  // =================================================================
  //
  //  Chaque commande est donnee a UN responsable (voir
  //  functions/src/services/repartition.service.ts). Ici :
  //    - « Je commence mon service » / « Terminer mon service » ;
  //    - les onglets ne montrent que MES commandes (et celles « a
  //      personne ») ;
  //    - celles de l'autre sont dans une vue a part, en lecture, avec
  //      « Reprendre » — en rouge quand il ne l'a pas prise en charge
  //      depuis 10 minutes.
  //  Un magasin a un seul responsable (Oulfa) ne voit rien de tout cela.
  const [equipe, setEquipe] = useState<Awaited<ReturnType<typeof api.orders.equipe>> | null>(null);
  const [vue, setVue] = useState<"miennes" | "autres">("miennes");
  const [serviceEnCours, setServiceEnCours] = useState(false);
  const [prenant, setPrenant] = useState<string | null>(null);

  async function chargerEquipe() {
    try {
      setEquipe(await api.orders.equipe());
    } catch {
      /* Sans l'equipe, l'ecran reste celui d'avant : tout est a moi. */
    }
  }

  useEffect(() => {
    void chargerEquipe();
    return intervalleVisible(() => void chargerEquipe(), 60000);
  }, []);

  const moi = user?.uid || "";
  const multi = (equipe?.equipe.length || 0) >= 2;
  const delai = equipe?.delaiMinutes || 10;
  const moiDansEquipe = equipe?.equipe.find((r) => r.uid === moi);
  const collegues = (equipe?.equipe || []).filter((r) => r.uid !== moi);
  const OUVERTS: string[] = [
    OrderStatus.PENDING,
    OrderStatus.GLOVO_ACCEPTED,
    OrderStatus.IN_PREPARATION,
    OrderStatus.GLOVO_READY,
  ];

  /** Minutes ecoulees depuis une date venue du serveur (Timestamp ou texte). */
  const minutesDepuis = (v: any): number | null => {
    if (!v) return null;
    const ms = v?._seconds ? v._seconds * 1000 : v?.seconds ? v.seconds * 1000 : new Date(v).getTime();
    if (!Number.isFinite(ms)) return null;
    const m = Math.floor((Date.now() - ms) / 60000);
    return m >= 0 ? m : null;
  };

  const estAMoi = (o: any) => !multi || !o.responsableId || o.responsableId === moi;
  const ordersMiens = orders.filter(estAMoi);
  const autresOuvertes = multi
    ? orders.filter((o: any) => o.responsableId && o.responsableId !== moi && OUVERTS.includes(o.status))
    : [];
  const enRetard = (o: any) => !o.priseEnChargeLe && (minutesDepuis(o.attribueLe) ?? 0) >= delai;
  const autresEnRetard = autresOuvertes.filter(enRetard).length;

  async function changerService(enService: boolean) {
    if (
      !enService &&
      !confirm(
        t("Terminer ton service ? Tes commandes en cours passent à l'autre responsable s'il est en service."),
      )
    )
      return;
    setServiceEnCours(true);
    try {
      const r = await api.orders.service(enService);
      await chargerEquipe();
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      if (enService) {
        handleSuccess(
          r.recues ? `${t("Bon service !")} ${r.recues} ${t("commande(s) reçue(s)")}` : t("Bon service !"),
        );
      } else {
        handleSuccess(
          r.gardees
            ? `${t("Service terminé.")} ${r.gardees} ${t("commande(s) restent à toi : aucun autre responsable en service.")}`
            : t("Service terminé."),
        );
      }
    } catch (error: any) {
      handleError(error, "Service");
    } finally {
      setServiceEnCours(false);
    }
  }

  // LA PAUSE DEJEUNER (15/10/2026) : 30 minutes, fin automatique. Ses
  // commandes en cours passent a l'autre ; un seul en pause a la fois.
  const heureDe = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "";
  const pauseEnCours = (r?: { enPause?: boolean; pauseJusqua?: string | null }) =>
    !!r?.enPause && !!r.pauseJusqua && new Date(r.pauseJusqua).getTime() > Date.now();
  const moiEnPause = pauseEnCours(moiDansEquipe);
  const collegueEnPause = collegues.find((c) => pauseEnCours(c));
  const collegueDisponible = collegues.some((c) => c.enService && !pauseEnCours(c) && !c.reposAujourdhui);
  // JOURS DE REPOS (15/10/2026) : le jour de repos de l'autre, tout est pour moi.
  const colleguesEnRepos = collegues.filter((c) => c.reposAujourdhui);
  const seulAujourdhui = collegues.length > 0 && colleguesEnRepos.length === collegues.length;

  async function changerPause(pause: boolean) {
    if (pause) {
      const mesOuvertes = ordersMiens.filter(
        (o: any) => o.responsableId === moi && OUVERTS.includes(o.status) && o.status !== OrderStatus.PENDING,
      ).length;
      const qui = collegues.filter((c) => c.enService && !pauseEnCours(c)).map((c) => c.nom).join(", ");
      if (
        !confirm(
          `${t("Partir en pause déjeuner 30 min ?")}` +
            (mesOuvertes ? `\n${mesOuvertes} ${t("commande(s) en cours passent à")} ${qui}.` : ""),
        )
      )
        return;
    }
    setServiceEnCours(true);
    try {
      const r = await api.orders.pause(pause);
      await chargerEquipe();
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      handleSuccess(
        pause
          ? `${t("Bon appétit ! Retour à")} ${heureDe(r.pauseJusqua)}` +
              (r.gardees ? ` — ${r.gardees} ${t("commande(s) restent à toi")}` : "")
          : t("Bon retour !"),
      );
    } catch (error: any) {
      handleError(error, "Pause");
    } finally {
      setServiceEnCours(false);
    }
  }

  // La pause se termine toute seule : on relit l'equipe a l'heure dite pour
  // que l'ecran repasse « en service » sans attendre.
  useEffect(() => {
    if (!moiEnPause || !moiDansEquipe?.pauseJusqua) return;
    const reste = new Date(moiDansEquipe.pauseJusqua).getTime() - Date.now() + 2000;
    const id = window.setTimeout(() => void chargerEquipe(), Math.max(1000, reste));
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moiEnPause, moiDansEquipe?.pauseJusqua]);

  /** « Je m'en occupe », « Je la prends » ou, avec `forcer`, « Reprendre ». */
  async function prendre(order: any, forcer: boolean) {
    if (forcer && !confirm(`${t("Reprendre la commande de")} ${order.responsableNom || ""} ?`)) return;
    setPrenant(order.id);
    try {
      const r = await api.orders.prendre(order.id, forcer);
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      if (r.statut === "reprise") {
        setVue("miennes");
        handleSuccess(t("Commande reprise"));
      } else {
        handleSuccess(t("C'est noté"));
      }
    } catch (error: any) {
      handleError(error, "Prendre la commande");
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    } finally {
      setPrenant(null);
    }
  }

  /** Le bandeau en tete de chaque carte : a personne, ou pas encore prise en charge. */
  const bandeau = (order: any) => {
    if (!multi) return null;
    if (!order.responsableId) {
      return (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
          <span className="text-xs font-bold text-amber-800">⚠️ {t("À personne")}</span>
          <button
            type="button"
            onClick={() => void prendre(order, false)}
            disabled={prenant === order.id}
            className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-black text-white hover:bg-amber-600 disabled:opacity-50"
          >
            {prenant === order.id ? "..." : t("Je la prends")}
          </button>
        </div>
      );
    }
    if (order.responsableId === moi && !order.priseEnChargeLe) {
      const m = minutesDepuis(order.attribueLe);
      return (
        <button
          type="button"
          onClick={() => void prendre(order, false)}
          disabled={prenant === order.id}
          className={`mb-2 w-full rounded-lg py-2.5 text-sm font-black text-white disabled:opacity-50 ${
            (m ?? 0) >= delai ? "bg-red-600 animate-pulse" : "bg-blue-600 animate-pulse"
          }`}
        >
          {prenant === order.id ? "..." : `✋ ${t("Je m'en occupe")}${m !== null ? ` · ${m} ${t("min")}` : ""}`}
        </button>
      );
    }
    return null;
  };

  const pendingOrders = ordersMiens.filter((o) => o.status === OrderStatus.PENDING);
  const inPreparationOrders = ordersMiens.filter(
    (o) => o.status === OrderStatus.IN_PREPARATION || o.status === OrderStatus.GLOVO_ACCEPTED
  );
  const readyOrders = ordersMiens.filter((o) => o.status === OrderStatus.GLOVO_READY);

  // Auto-sélectionner l'onglet avec des commandes
  useEffect(() => {
    if (pendingOrders.length > 0) {
      setActiveTab("pending");
    } else if (inPreparationOrders.length > 0) {
      setActiveTab("inPreparation");
    } else if (readyOrders.length > 0) {
      setActiveTab("ready");
    }
  }, [pendingOrders.length, inPreparationOrders.length, readyOrders.length]);

  if (userStoreId === "loading" || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loading />
      </div>
    );
  }
  if (userStoreId === null) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <p className="text-amber-800 font-medium">{t("Aucun store associé à votre compte")}</p>
        <p className="text-sm text-amber-700 mt-2">{t("Contactez l'administrateur pour configurer votre accès.")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      {/* Header avec gradient - PLEIN ÉCRAN collé en haut (pas de marge) */}
      <div className="bg-gradient-to-r from-primary to-primary px-3 py-3 md:px-6 md:py-6 shadow-lg mb-3 -mx-4 -mt-4 md:mx-0 md:mt-0">
        <div className="flex items-center gap-2 mb-2 px-1">
          <div className="bg-white/20 p-1.5 md:p-2 rounded-lg">
            <Users className="w-4 h-4 md:w-6 md:h-6 text-white" />
          </div>
          <h1 className="text-lg md:text-3xl font-bold text-white">{t("Dashboard")}</h1>
        </div>
        
        {/* Stats compactes - TRÈS COMPACT sur mobile */}
        <div className="flex items-center justify-between bg-white/10 rounded-lg p-2 md:p-3 backdrop-blur-sm gap-2 mx-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="bg-amber-500 p-1.5 md:p-2 rounded-lg flex-shrink-0">
              <AlertCircle className="w-3 h-3 md:w-4 md:h-4 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-lg md:text-2xl font-black text-white leading-none">{pendingOrders.length}</p>
              <p className="text-[9px] md:text-xs text-white/80 font-medium truncate">{t("À accepter")}</p>
            </div>
          </div>
          
          <div className="w-px h-8 md:h-10 bg-white/20 flex-shrink-0"></div>
          
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="bg-purple-500 p-1.5 md:p-2 rounded-lg flex-shrink-0">
              <Timer className="w-3 h-3 md:w-4 md:h-4 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-lg md:text-2xl font-black text-white leading-none">{inPreparationOrders.length}</p>
              <p className="text-[9px] md:text-xs text-white/80 font-medium truncate">{t("En prép.")}</p>
            </div>
          </div>
          
          <div className="w-px h-8 md:h-10 bg-white/20 flex-shrink-0"></div>
          
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="bg-green-500 p-1.5 md:p-2 rounded-lg flex-shrink-0">
              <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-lg md:text-2xl font-black text-white leading-none">{readyOrders.length}</p>
              <p className="text-[9px] md:text-xs text-white/80 font-medium truncate">{t("Prêtes")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* LE SERVICE ET LES DEUX VUES (15/10/2026) — seulement quand le
          magasin a plusieurs responsables. */}
      {multi && (
        <div className="px-3 md:px-6 mt-3 space-y-2">
          {moiDansEquipe?.enService && moiEnPause ? (
            <div className="flex items-center justify-between gap-2 rounded-xl border-2 border-sky-300 bg-sky-50 px-3 py-2.5">
              <p className="text-sm font-bold text-sky-800">
                🍽️ {t("En pause jusqu'à")} {heureDe(moiDansEquipe.pauseJusqua)}
                <span className="block text-[11px] font-semibold text-sky-700">
                  {t("Les nouvelles commandes vont à l'autre responsable.")}
                </span>
              </p>
              <button
                type="button"
                onClick={() => void changerPause(false)}
                disabled={serviceEnCours}
                className="shrink-0 rounded-lg bg-sky-600 px-3 py-2 text-xs font-black text-white hover:bg-sky-700 disabled:opacity-50"
              >
                {serviceEnCours ? "..." : t("Je reviens")}
              </button>
            </div>
          ) : moiDansEquipe?.enService ? (
            <div className="flex items-center justify-between gap-2 rounded-xl border-2 border-emerald-300 bg-emerald-50 px-3 py-2.5">
              <p className="text-sm font-bold text-emerald-800">
                🟢 {t("En service depuis")}{" "}
                {moiDansEquipe.depuis
                  ? new Date(moiDansEquipe.depuis).toLocaleTimeString("fr-FR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : ""}
              </p>
              <div className="flex shrink-0 flex-col gap-1.5 sm:flex-row">
                {/* Un seul en pause a la fois, et jamais seul en service. */}
                <button
                  type="button"
                  onClick={() => void changerPause(true)}
                  disabled={serviceEnCours || !!collegueEnPause || !collegueDisponible}
                  title={
                    collegueEnPause
                      ? `${collegueEnPause.nom} ${t("est en pause jusqu'à")} ${heureDe(collegueEnPause.pauseJusqua)}`
                      : !collegueDisponible
                        ? t("Personne d'autre n'est en service")
                        : ""
                  }
                  className="rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-sky-700 disabled:bg-gray-300 disabled:text-gray-500"
                >
                  🍽️ {t("Pause déjeuner")}
                </button>
                <button
                  type="button"
                  onClick={() => void changerService(false)}
                  disabled={serviceEnCours}
                  className="rounded-lg border border-emerald-400 bg-white px-3 py-1.5 text-xs font-bold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                >
                  {serviceEnCours ? "..." : t("Terminer mon service")}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => void changerService(true)}
              disabled={serviceEnCours}
              className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3 text-base font-black text-white shadow-lg active:scale-95 disabled:opacity-50"
            >
              {serviceEnCours ? "..." : `▶️ ${t("Je commence mon service")}`}
              <span className="mt-0.5 block text-[11px] font-semibold text-white/90">
                {seulAujourdhui
                  ? `${colleguesEnRepos.map((c) => c.nom).join(", ")} ${t("est en repos aujourd'hui : toutes les commandes sont pour toi.")}`
                  : `${t("Tant que tu n'es pas en service, les nouvelles commandes vont à")} ${collegues
                      .filter((c) => !c.reposAujourdhui)
                      .map((c) => c.nom)
                      .join(", ")}`}
              </span>
            </button>
          )}

          {moiDansEquipe?.enService && !moiEnPause && (collegueEnPause || !collegueDisponible) && (
            <p className="text-[11px] font-semibold text-gray-500">
              🍽️{" "}
              {collegueEnPause
                ? `${collegueEnPause.nom} ${t("est en pause jusqu'à")} ${heureDe(collegueEnPause.pauseJusqua)} — ${t("ta pause sera possible à son retour.")}`
                : seulAujourdhui
                  ? `${colleguesEnRepos.map((c) => c.nom).join(", ")} ${t("est en repos aujourd'hui : pas de pause possible, personne pour te relayer.")}`
                  : t("Pause possible quand l'autre responsable est en service.")}
            </p>
          )}

          {/* Qui d'autre est la. */}
          <div className="flex flex-wrap gap-1.5">
            {collegues.map((c) => {
              const n = autresOuvertes.filter((o: any) => o.responsableId === c.uid).length;
              return (
                <span
                  key={c.uid}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                    c.enService
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-gray-200 bg-gray-50 text-gray-500"
                  }`}
                >
                  {c.reposAujourdhui ? "🛌" : pauseEnCours(c) ? "🍽️" : c.enService ? "🟢" : "⚪"} {c.nom} ·{" "}
                  {c.reposAujourdhui
                    ? t("repos aujourd'hui")
                    : pauseEnCours(c)
                      ? `${t("en pause jusqu'à")} ${heureDe(c.pauseJusqua)}`
                      : c.enService
                        ? t("en service")
                        : t("Pas en service")}{" "}
                  · {n} {t("en cours")}
                </span>
              );
            })}
          </div>

          {/* Mes commandes / celles des autres. */}
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setVue("miennes")}
              className={`flex-1 rounded-lg px-3 py-2 text-xs font-black md:text-sm ${
                vue === "miennes" ? "bg-primary text-white shadow" : "border border-gray-200 bg-white text-gray-600"
              }`}
            >
              {t("Mes commandes")} ({ordersMiens.filter((o: any) => OUVERTS.includes(o.status)).length})
            </button>
            <button
              type="button"
              onClick={() => setVue("autres")}
              className={`flex-1 rounded-lg px-3 py-2 text-xs font-black md:text-sm ${
                vue === "autres" ? "bg-gray-800 text-white shadow" : "border border-gray-200 bg-white text-gray-600"
              }`}
            >
              {collegues.length === 1 ? collegues[0].nom : t("Les autres")} ({autresOuvertes.length})
              {autresEnRetard > 0 && (
                <span className="ml-1.5 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] text-white animate-pulse">
                  {autresEnRetard} ⚠️
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* QUI EST LA, avant tout le reste : une commande envoyee a un
          fournisseur deconnecte ne sera pas preparee. Le serveur ne renvoie
          que les fournisseurs du magasin de celui qui regarde. */}
      <div className="px-3 md:px-6 mt-3">
        <PresenceFournisseurs />
      </div>

      {/* Volaille achetee en caisse : le prix du kilo du jour (18/10/2026).
          N'apparait que dans un magasin qui en a. */}
      <PrixVolailleCaisse onEnregistre={() => void chargerAchats()} />

      {/* Les achats a faire, AVANT les commandes : tant qu'un achat n'est
          pas fait, la commande correspondante ne peut pas partir. */}
      {achats.length > 0 && (
        <div className="px-3 md:px-6 mb-4">
          <div className="bg-white rounded-xl border-2 border-red-300 shadow-md overflow-hidden">
            <div className="bg-red-50 px-4 py-3 border-b border-red-200">
              <p className="font-black text-red-800 uppercase tracking-wide text-sm">
                {achats.length} article{achats.length > 1 ? "s" : ""} à acheter
              </p>
              <p className="text-xs text-red-700 mt-0.5">
                Le fournisseur ne les avait pas. Achète-les avec la caisse.
              </p>
            </div>

            <div className="divide-y divide-gray-100">
              {achats.map((a) => (
                <div key={a.id} className="p-4">
                  <div className="flex items-center gap-3">
                    {a.photo ? (
                      <img
                        src={photoReduite(a.photo, 160)}
                        alt={a.article}
                        className="w-14 h-14 flex-shrink-0 rounded-lg object-cover border border-gray-200 bg-gray-100"
                      />
                    ) : (
                      <div className="w-14 h-14 flex-shrink-0 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                        <Package className="w-6 h-6" />
                      </div>
                    )}
                    <p className="font-bold text-gray-900">
                      {a.quantite} × {a.article}
                    </p>
                  </div>
                  {/* Le code de retrait, pas le numero de commande : c'est
                      celui que le livreur annonce et que Hakim reconnait. */}
                  <p className="text-xs text-gray-500">
                    {a.codeRetrait ? (
                      <>
                        code{" "}
                        <span className="font-mono font-bold text-gray-700">
                          {a.codeRetrait}
                        </span>
                      </>
                    ) : (
                      `commande #${a.numero}`
                    )}
                  </p>

                  {/* Le maximum a l'unite ET le total : devant un marchand,
                      les deux se confondent vite quand on est presse. */}
                  <p className="text-sm font-bold text-orange-700 mt-1">
                    maximum {(a.prixMaxCentimes / 100).toFixed(2)} DH l'unité
                    {a.quantite > 1 && (
                      <span className="font-medium text-orange-600">
                        {" "}
                        — {((a.prixMaxCentimes * a.quantite) / 100).toFixed(2)} DH
                        au total
                      </span>
                    )}
                  </p>

                  {a.auPoids ? (
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <span className="text-sm font-bold text-gray-800">⚖️ Poids pesé</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        value={poidsAchats[a.id] ?? ""}
                        onChange={(e) => setPoidsAchats((p) => ({ ...p, [a.id]: e.target.value }))}
                        placeholder="kg (1.45)"
                        className="w-28 text-sm border-2 border-amber-300 rounded-lg px-3 py-2 font-bold"
                      />
                      {a.prixKgConnu ? (
                        <span className="text-xs text-gray-600">× {a.prixKgConnu} DH/kg</span>
                      ) : (
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.5"
                          value={prixKgAchats[a.id] ?? ""}
                          onChange={(e) => setPrixKgAchats((p) => ({ ...p, [a.id]: e.target.value }))}
                          placeholder="prix du kg"
                          className="w-28 text-sm border border-gray-300 rounded-lg px-3 py-2"
                        />
                      )}
                      {Number((poidsAchats[a.id] || "").replace(",", ".")) > 0 &&
                        (a.prixKgConnu || Number((prixKgAchats[a.id] || "").replace(",", ".")) > 0) && (
                          <span className="text-xs font-bold text-emerald-700">
                            ={" "}
                            {(
                              Number((poidsAchats[a.id] || "").replace(",", ".")) *
                                (a.prixKgConnu || Number((prixKgAchats[a.id] || "").replace(",", "."))) +
                              2 * Math.max(1, Number(a.quantite) || 1)
                            ).toFixed(2)}{" "}
                            DH (dont 2 DH/poulet d'abattage)
                          </span>
                        )}
                      <button
                        type="button"
                        onClick={() => void solder(a, false)}
                        disabled={achatEnCours === a.id || !(Number((poidsAchats[a.id] || "").replace(",", ".")) > 0)}
                        className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-200 disabled:text-gray-400"
                      >
                        {achatEnCours === a.id ? "…" : "Pesé et acheté"}
                      </button>
                      <button
                        type="button"
                        onClick={() => void solder(a, true)}
                        disabled={achatEnCours === a.id}
                        className="text-xs font-semibold text-gray-500 hover:text-red-600 underline disabled:opacity-40"
                      >
                        introuvable
                      </button>
                    </div>
                  ) : (
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      value={prixPayes[a.id] ?? ""}
                      onChange={(e) =>
                        setPrixPayes((p) => ({ ...p, [a.id]: e.target.value }))
                      }
                      placeholder="prix payé"
                      className="w-28 text-sm border border-gray-300 rounded-lg px-3 py-2"
                    />
                    <button
                      type="button"
                      onClick={() => void solder(a, false)}
                      disabled={
                        achatEnCours === a.id || !(Number((prixPayes[a.id] || "").replace(",", ".")) > 0)
                      }
                      className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-200 disabled:text-gray-400"
                    >
                      {achatEnCours === a.id ? "…" : "Acheté"}
                    </button>
                    <button
                      type="button"
                      onClick={() => void solder(a, true)}
                      disabled={achatEnCours === a.id}
                      className="text-xs font-semibold text-gray-500 hover:text-red-600 underline disabled:opacity-40"
                    >
                      introuvable
                    </button>
                    {/* L'ERREUR DE DOIGT. Le fournisseur avait l'article :
                        rien a acheter, il le prepare. */}
                    <button
                      type="button"
                      onClick={() => void annulerErreur(a)}
                      disabled={achatEnCours === a.id}
                      className="px-3 py-2 rounded-lg text-xs font-bold text-blue-700 border border-blue-300 bg-blue-50 hover:bg-blue-100 disabled:opacity-40"
                    >
                      Erreur du fournisseur — il l&apos;a
                    </button>
                  </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="px-3 md:px-6">
        {multi && vue === "autres" ? (
          <div className="space-y-3">
            {autresOuvertes.length === 0 && (
              <div className="rounded-2xl border border-gray-100 bg-white p-8 text-center text-sm text-gray-500 shadow">
                {t("Aucune commande en cours chez")} {collegues.map((c) => c.nom).join(", ")}
              </div>
            )}
            {autresOuvertes.map((order: any) => {
              const baskets = order.baskets || [];
              const recuperes = baskets.filter((b: any) => b.status === "PICKED_UP").length;
              const prets = baskets.filter((b: any) => b.status === "READY").length;
              const retard = enRetard(order);
              const m = minutesDepuis(order.attribueLe);
              return (
                <div
                  key={order.id}
                  className={`rounded-xl border-2 bg-white p-3 shadow-sm ${retard ? "border-red-400" : "border-gray-200"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="rounded-lg bg-gray-100 px-2.5 py-1 font-mono text-base font-black text-gray-800">
                          {order.metadata?.pick_up_code || order.pickUpCode || "—"}
                        </span>
                        <NumeroCommande commande={order} />
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_COLORS[order.status as OrderStatus]}`}
                        >
                          {t(STATUS_LABELS[order.status as OrderStatus])}
                        </span>
                      </div>
                      <p className="mt-1 text-sm font-semibold text-gray-900">{order.customerName}</p>
                      <p className="text-xs font-bold text-gray-700">👤 {order.responsableNom || "—"}</p>
                      <p className={`mt-0.5 text-xs font-semibold ${retard ? "text-red-700" : "text-gray-500"}`}>
                        {order.priseEnChargeLe
                          ? `✅ ${t("prise en charge")}`
                          : `${retard ? "⚠️ " : ""}${t("pas prise en charge depuis")} ${m ?? 0} ${t("min")}`}
                        {baskets.length > 0 ? ` · 📦 ${recuperes}/${baskets.length}` : ""}
                        {prets > 0 ? ` · ✅ ${prets} ${t("prêt")}${prets > 1 ? "s" : ""}` : ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void prendre(order, true)}
                      disabled={prenant === order.id}
                      className={`shrink-0 rounded-lg px-3 py-2 text-xs font-black disabled:opacity-50 ${
                        retard
                          ? "bg-red-600 text-white animate-pulse"
                          : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {prenant === order.id ? "..." : t("Reprendre")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
        <>
        {/* Onglets - COMPACT sur mobile */}
        <div className="flex gap-1.5 md:gap-2 mb-3 md:mb-4 overflow-x-auto pb-1">
          <button
            onClick={() => setActiveTab("pending")}
            className={`flex-1 px-2 py-2 md:px-4 md:py-3 rounded-lg md:rounded-xl font-bold text-xs md:text-sm transition-all ${
              activeTab === "pending"
                ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg"
                : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
            }`}
          >
            <div className="flex items-center justify-center gap-1 md:gap-2">
              <AlertCircle className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
              <span className="hidden md:inline">{t("À accepter")}</span>
              <span className="md:hidden">{t("Accepter")}</span>
              {pendingOrders.length > 0 && (
                <span className={`px-1.5 md:px-2 py-0.5 rounded-full text-[10px] md:text-xs font-black ${
                  activeTab === "pending" ? "bg-white/30" : "bg-amber-100 text-amber-700"
                }`}>
                  {pendingOrders.length}
                </span>
              )}
            </div>
          </button>

          <button
            onClick={() => setActiveTab("inPreparation")}
            className={`flex-1 px-2 py-2 md:px-4 md:py-3 rounded-lg md:rounded-xl font-bold text-xs md:text-sm transition-all ${
              activeTab === "inPreparation"
                ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg"
                : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
            }`}
          >
            <div className="flex items-center justify-center gap-1 md:gap-2">
              <Timer className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
              <span className="hidden md:inline">{t("En préparation")}</span>
              <span className="md:hidden">{t("Prép.")}</span>
              {inPreparationOrders.length > 0 && (
                <span className={`px-1.5 md:px-2 py-0.5 rounded-full text-[10px] md:text-xs font-black ${
                  activeTab === "inPreparation" ? "bg-white/30" : "bg-purple-100 text-purple-700"
                }`}>
                  {inPreparationOrders.length}
                </span>
              )}
            </div>
          </button>

          <button
            onClick={() => setActiveTab("ready")}
            className={`flex-1 px-2 py-2 md:px-4 md:py-3 rounded-lg md:rounded-xl font-bold text-xs md:text-sm transition-all ${
              activeTab === "ready"
                ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg"
                : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
            }`}
          >
            <div className="flex items-center justify-center gap-1 md:gap-2">
              <CheckCircle className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
              <span>{t("Prêtes")}</span>
              {readyOrders.length > 0 && (
                <span className={`px-1.5 md:px-2 py-0.5 rounded-full text-[10px] md:text-xs font-black ${
                  activeTab === "ready" ? "bg-white/30" : "bg-green-100 text-green-700"
                }`}>
                  {readyOrders.length}
                </span>
              )}
            </div>
          </button>
        </div>

      {/* Commandes en attente - Cartes séparées sans conteneur */}
      {activeTab === "pending" && (
        <div className="space-y-3">
          {pendingOrders.length === 0 ? (
            // Placeholder géré par la section commune plus bas
            null
          ) : (
            pendingOrders.map((order) => (
              <div
                key={order.id}
                className="bg-white border border-gray-200 rounded-xl p-3 md:p-4 hover:shadow-lg hover:border-gray-300 transition-all duration-200 shadow-sm"
              >
                {bandeau(order)}
                <div className="flex items-start justify-between gap-2">
                  {/* Info commande - COMPACT sur mobile, nom complet sans prix */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 md:gap-2 flex-wrap mb-1.5 md:mb-2">
                      <span className="font-black text-base md:text-xl text-primary font-mono bg-primary/5 px-2 py-1 md:px-4 md:py-2 rounded-lg">
                        {(order as any).metadata?.pick_up_code || (order as any).pickUpCode || "—"}
                      </span>
                      <NumeroCommande commande={order} />
                      <span className={`px-2 py-0.5 md:px-3 md:py-1 text-[10px] md:text-xs font-bold rounded-full ${STATUS_COLORS[order.status as OrderStatus]}`}>
                        {t(STATUS_LABELS[order.status as OrderStatus])}
                      </span>
                    </div>
                    {/* Nom complet sans prix */}
                    <p className="text-sm md:text-base text-gray-900 font-semibold">{order.customerName}</p>
                    <p className="text-[10px] md:text-xs text-gray-500 font-medium flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3 md:w-3.5 md:h-3.5" />
                      {formatDate((order as any).orderTime || order.createdAt)}
                    </p>
                  </div>
                  
                  {/* Actions compactes - 2 boutons seulement */}
                  <div className="flex gap-1.5 md:gap-2 flex-shrink-0">
                    {/* Accepter - Vert */}
                    <button
                      onClick={() => handleAccept(order.id)}
                      disabled={processing === order.id}
                      className="p-2 md:p-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg md:rounded-xl hover:from-green-700 hover:to-emerald-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
                      title={t("Accepter")}
                    >
                      <CheckCircle className="w-4 h-4 md:w-5 md:h-5" />
                    </button>
                    
                    {/* Détails - Gris */}
                    <button
                      onClick={() => handleOpenDetails(order)}
                      className="p-2 md:p-3 bg-gray-100 text-gray-700 rounded-lg md:rounded-xl hover:bg-gray-200 active:scale-95 transition-all border border-gray-200"
                      title={t("Voir détails")}
                    >
                      <Eye className="w-4 h-4 md:w-5 md:h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Commandes en préparation */}
      {activeTab === "inPreparation" && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
          {inPreparationOrders.length === 0 ? (
            // Placeholder géré par la section commune plus bas
            null
          ) : (
            <div className="p-3 md:p-4 space-y-3">
            {inPreparationOrders.map((order) => {
              const orderWithDetails = order as any;
              const baskets = orderWithDetails.baskets || [];
              // Un panier ANNULE (article passe en caisse) n'a plus rien a recuperer.
              const allPickedUp = baskets.length > 0 && baskets.every((b: any) => b.status === "PICKED_UP" || b.status === "CANCELLED");
              
              const readyBaskets = baskets.filter((b: any) => b.status === "READY");
              const pickedUpBaskets = baskets.filter((b: any) => b.status === "PICKED_UP");
              const pendingBaskets = baskets.filter((b: any) => b.status === "PENDING" || b.status === "PREPARING");
              // PAS COMMENCE et EN PREPARATION sont deux choses differentes.
              //
              // Avant le bouton COMMENCER, la distinction n'existait pas :
              // tout restait « en attente » jusqu'a « PRÊT ». Maintenant,
              // c'est la seule information qui appelle une action du
              // responsable — un panier que personne n'a ouvert.
              //
              // `pendingBaskets` n'est pas touche : il alimente la barre de
              // progression et les pastilles, qui comptent bien les deux.
              const pasCommences = baskets.filter((b: any) => b.status === "PENDING");
              const enPreparation = baskets.filter((b: any) => b.status === "PREPARING");

              /** Depuis combien de minutes ce panier attend-il ? */
              const attenteMinutes = (b: any): number | null => {
                const d = b?.createdAt;
                if (!d) return null;
                // Surtout pas `t` : c'est le nom de la traduction.
                const depuis = new Date(
                  d?._seconds ? d._seconds * 1000 : d,
                ).getTime();
                if (!Number.isFinite(depuis)) return null;
                const m = Math.floor((Date.now() - depuis) / 60000);
                return m >= 0 ? m : null;
              };

              return (
                <div
                  key={order.id}
                  className="group bg-gradient-to-br from-white to-purple-50/30 border-2 border-purple-200 rounded-xl p-4 hover:shadow-lg hover:border-purple-400 transition-all duration-200"
                >
                  <div className="space-y-4">
                    {bandeau(order)}
                    {/* Info commande */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="font-black text-xl text-primary font-mono bg-primary/5 px-4 py-2 rounded-lg">
                          {(order as any).metadata?.pick_up_code || (order as any).pickUpCode || "—"}
                        </span>
                        <NumeroCommande commande={order} />
                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${STATUS_COLORS[order.status as OrderStatus]}`}>
                          {t(STATUS_LABELS[order.status as OrderStatus])}
                        </span>
                        
                        {/* Indicateurs de paniers */}
                        {baskets.length > 0 && (
                          <div className="flex items-center gap-1">
                            {pendingBaskets.length > 0 && (
                              <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {pendingBaskets.length} {t("en préparation")}
                              </span>
                            )}
                            {readyBaskets.length > 0 && (
                              <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 flex items-center gap-1 animate-pulse">
                                <CheckCircle className="w-3 h-3" />
                                {readyBaskets.length} {t("prêt")}{readyBaskets.length > 1 ? "s" : ""}
                              </span>
                            )}
                            {pickedUpBaskets.length > 0 && (
                              <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 flex items-center gap-1">
                                <Package className="w-3 h-3" />
                                {pickedUpBaskets.length} {t("récupéré")}{pickedUpBaskets.length > 1 ? "s" : ""}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <p className="text-gray-700 font-semibold">{order.customerName}</p>
                        {/* Pas de prix pour le responsable commande (02/11/2026). */}
                      </div>
                      <p className="text-xs text-gray-500 font-medium flex items-center gap-1 mt-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDate((order as any).orderTime || order.createdAt)}
                      </p>
                      
                      {/* Barre de progression */}
                      {baskets.length > 0 && (
                        <div className="mt-3 bg-white/80 border border-purple-200 rounded-xl p-3 shadow-sm">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold text-gray-700 flex items-center gap-1">
                              <Package className="w-4 h-4" />
                              {t("État des paniers")}
                            </span>
                            <span className="text-sm font-black text-purple-600">{pickedUpBaskets.length + readyBaskets.length}/{baskets.length}</span>
                          </div>
                          
                          <div className="w-full bg-gray-200 rounded-full h-3 mb-2 shadow-inner">
                            <div 
                              className="bg-gradient-to-r from-purple-500 to-purple-600 h-3 rounded-full transition-all duration-500 flex items-center justify-end pr-1"
                              style={{ width: `${((pickedUpBaskets.length + readyBaskets.length) / baskets.length) * 100}%` }}
                            >
                              {((pickedUpBaskets.length + readyBaskets.length) / baskets.length) * 100 >= 20 && (
                                <span className="text-[10px] font-bold text-white">
                                  {Math.round(((pickedUpBaskets.length + readyBaskets.length) / baskets.length) * 100)}%
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* LES NOMS, pas seulement les comptes.
                              « 1 PRÊT » ne dit pas chez qui aller : il fallait
                              ouvrir Details pour l'apprendre. Le nom du
                              fournisseur repond a la seule question que se pose
                              le responsable a ce moment-la. */}
                          <div className="space-y-1.5 text-xs">
                            {readyBaskets.length > 0 && (
                              <div className="flex items-start gap-2">
                                <span className="text-green-700 font-bold shrink-0">
                                  ✅ À récupérer
                                </span>
                                <span className="text-green-800 font-bold">
                                  {readyBaskets
                                    .map((b: any) => b.fournisseurName || "Fournisseur")
                                    .join(", ")}
                                </span>
                              </div>
                            )}
                            {/* PAS COMMENCÉ — la seule ligne qui demande une
                                action au responsable. Elle passe donc devant
                                les autres, qui ne font que l'informer.

                                Le temps d'attente est indispensable : un
                                panier arrive il y a une minute n'est pas un
                                probleme, le meme vingt minutes plus tard en
                                est un. Rouge a partir de 10 minutes. */}
                            {/* Combien d'articles de CETTE commande partent en
                                caisse. Le bloc rouge du haut dit « 1 article a
                                acheter » sans dire de quelle commande ; ici,
                                Hakim voit tout de suite laquelle est
                                incomplete. */}
                            {(() => {
                              const n = achats.filter(
                                (a: any) => a.orderId === order.id,
                              ).length;
                              if (n === 0) return null;
                              return (
                                <div className="flex items-start gap-2">
                                  <span className="text-red-700 font-bold shrink-0">
                                    🛒 À acheter en caisse
                                  </span>
                                  <span className="text-red-800 font-semibold">
                                    {n} article{n > 1 ? "s" : ""}
                                  </span>
                                </div>
                              );
                            })()}
                            {pasCommences.length > 0 && (
                              <div className="flex items-start gap-2">
                                <span
                                  className={`shrink-0 font-bold ${
                                    pasCommences.some(
                                      (b: any) => (attenteMinutes(b) ?? 0) >= 10,
                                    )
                                      ? "text-red-700"
                                      : "text-orange-700"
                                  }`}
                                >
                                  🔴 {t("Pas commencé")}
                                </span>
                                <span className="font-semibold">
                                  {pasCommences.map((b: any, i: number) => {
                                    const m = attenteMinutes(b);
                                    const urgent = (m ?? 0) >= 10;
                                    return (
                                      <span
                                        key={b.id || i}
                                        className={
                                          urgent ? "text-red-700" : "text-orange-800"
                                        }
                                      >
                                        {i > 0 ? ", " : ""}
                                        {b.fournisseurName || t("Fournisseur")}
                                        {m !== null ? ` (${m} ${t("min")})` : ""}
                                      </span>
                                    );
                                  })}
                                </span>
                              </div>
                            )}
                            {enPreparation.length > 0 && (
                              <div className="flex items-start gap-2">
                                <span className="text-yellow-700 shrink-0">
                                  ⏳ En préparation
                                </span>
                                <span className="text-yellow-800">
                                  {enPreparation
                                    .map((b: any) => b.fournisseurName || "Fournisseur")
                                    .join(", ")}
                                </span>
                              </div>
                            )}
                            {pickedUpBaskets.length > 0 && (
                              <div className="flex items-start gap-2">
                                <span className="text-purple-700 shrink-0">📦 Récupéré</span>
                                <span className="text-purple-800">
                                  {pickedUpBaskets
                                    .map((b: any) => b.fournisseurName || "Fournisseur")
                                    .join(", ")}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleOpenDetails(order)}
                        className="flex-1 md:flex-none px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 active:scale-95 transition-all font-semibold text-sm flex items-center justify-center gap-2 border border-gray-200"
                      >
                        <Eye className="w-4 h-4" />
                        {t("Détails")}
                      </button>
                      <button
                        onClick={() => handleMarkReady(order.id)}
                        disabled={processing === order.id || !allPickedUp}
                        className="flex-1 md:flex-none px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-black text-sm flex items-center justify-center gap-2 shadow-md"
                      >
                        <CheckCircle className="w-5 h-5" />
                        {processing === order.id ? "..." : t("MARQUER PRÊTE")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          )}
        </div>
      )}

      {/* Commandes prêtes */}
      {activeTab === "ready" && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
          {readyOrders.length === 0 ? (
            // Placeholder géré par la section commune plus bas
            null
          ) : (
            <div className="p-3 md:p-4 space-y-3">
            {readyOrders.map((order) => {
              const orderWithDetails = order as any;
              const baskets = orderWithDetails.baskets || [];
              const pickedUpBaskets = baskets.filter((b: any) => b.status === "PICKED_UP");
              const readyBaskets = baskets.filter((b: any) => b.status === "READY");
              
              return (
                <div
                  key={order.id}
                  className="group bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-4 hover:shadow-lg hover:border-green-400 transition-all duration-200"
                >
                  {bandeau(order)}
                  <div className="flex flex-col md:flex-row md:items-center gap-3">
                    {/* Info commande */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="font-black text-xl text-green-700 font-mono bg-white px-4 py-2 rounded-lg shadow-sm">
                          {(orderWithDetails as any).metadata?.pick_up_code || (orderWithDetails as any).pickUpCode || "—"}
                        </span>
                        <NumeroCommande commande={orderWithDetails} />
                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${STATUS_COLORS[order.status as OrderStatus]}`}>
                          {t(STATUS_LABELS[order.status as OrderStatus])}
                        </span>
                        
                        {/* Indicateurs de paniers */}
                        {baskets.length > 0 && (
                          <div className="flex items-center gap-1">
                            {readyBaskets.length > 0 && (
                              <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                {readyBaskets.length} {t("prêt")}{readyBaskets.length > 1 ? "s" : ""}
                              </span>
                            )}
                            {pickedUpBaskets.length > 0 && (
                              <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 flex items-center gap-1">
                                <Package className="w-3 h-3" />
                                {pickedUpBaskets.length} {t("récupéré")}{pickedUpBaskets.length > 1 ? "s" : ""}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 font-semibold">{order.customerName}</p>
                      <p className="text-xs text-green-700 font-semibold flex items-center gap-1 mt-1">
                        <CheckCircle className="w-3.5 h-3.5" />
                        {t("Prête depuis")} {formatDate(orderWithDetails.readyAt)}
                      </p>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleOpenDetails(order)}
                        className="flex-1 md:flex-none px-4 py-2.5 bg-white text-green-700 rounded-xl hover:bg-green-100 active:scale-95 transition-all font-semibold text-sm flex items-center justify-center gap-2 border-2 border-green-300 shadow-sm"
                      >
                        <Eye className="w-4 h-4" />
                        {t("Détails")}
                      </button>
                      <button
                        onClick={() => handleMarkDelivered(order.id)}
                        disabled={processing === order.id}
                        className="flex-1 md:flex-none px-5 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-black text-sm flex items-center justify-center gap-2 shadow-md"
                      >
                        <CheckCircle className="w-5 h-5" />
                        {processing === order.id ? "..." : t("LIVRÉE")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          )}
        </div>
      )}

      {/* État vide - affiché seulement si l'onglet actif n'a aucune commande */}
      {((activeTab === "pending" && pendingOrders.length === 0) ||
        (activeTab === "inPreparation" && inPreparationOrders.length === 0) ||
        (activeTab === "ready" && readyOrders.length === 0)) &&
        ordersMiens.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-100">
          <div className="bg-gradient-to-br from-gray-100 to-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            {activeTab === "pending" && <AlertCircle className="w-10 h-10 text-gray-400" />}
            {activeTab === "inPreparation" && <Timer className="w-10 h-10 text-gray-400" />}
            {activeTab === "ready" && <CheckCircle className="w-10 h-10 text-gray-400" />}
          </div>
          <h3 className="text-lg font-bold text-gray-700 mb-2">
            {activeTab === "pending" && t("Aucune commande en attente")}
            {activeTab === "inPreparation" && t("Aucune commande en préparation")}
            {activeTab === "ready" && t("Aucune commande prête")}
          </h3>
          <p className="text-sm text-gray-500">{t("Les commandes apparaîtront ici automatiquement")}</p>
        </div>
      )}

      {/* État vide - aucune commande du tout */}
      {ordersMiens.length === 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center border border-gray-100">
          <div className="bg-gradient-to-br from-gray-100 to-gray-50 w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="w-10 h-10 md:w-12 md:h-12 text-gray-400" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-gray-700 mb-2">{t("Aucune commande")}</h3>
          <p className="text-sm text-gray-500">{t("Les nouvelles commandes apparaîtront ici automatiquement")}</p>
        </div>
      )}
        </>
        )}
      </div>

      {/* Modal détails - style admin, infos complètes */}
      {selectedOrder && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 md:p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedOrder(null);
          }}
        >
          <div className="bg-white rounded-t-3xl md:rounded-3xl max-w-2xl w-full max-h-[95vh] md:max-h-[90vh] overflow-hidden shadow-2xl flex flex-col animate-slide-up">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-primary to-primary px-4 md:px-6 py-4 md:py-5 flex justify-between items-center shadow-lg z-10">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-xl">
                  <ShoppingBag className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-white">{t("Détails commande")}</h2>
                  <p className="text-xs text-white/80 font-medium">
                    {(selectedOrder as any).orderCode || selectedOrder.externalId || selectedOrder.id.slice(0, 8)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="p-2 rounded-xl text-white/90 hover:bg-white/20 active:scale-95 transition-all"
              >
                <XCircle className="w-6 h-6 md:w-7 md:h-7" />
              </button>
            </div>
            
            {/* Content scrollable */}
            <div className="flex-1 p-4 md:p-6 overflow-y-auto space-y-5">
              {/* Info générale */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-4 border-2 border-gray-100 shadow-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">Order Code</label>
                    <p className="text-sm md:text-base text-gray-900 font-mono font-bold bg-gray-100 px-2 py-1 rounded">
                      {(selectedOrder as any).orderCode || selectedOrder.externalId || "—"}
                    </p>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">{t("Date")}</label>
                    <p className="text-sm md:text-base text-gray-900 font-semibold">
                      {formatDate((selectedOrder as any).orderTime || (selectedOrder as any).createdAt)}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">{t("Code Retrait")}</label>
                    <p className="text-lg md:text-xl text-primary font-black font-mono bg-primary/5 px-3 py-1.5 rounded-lg inline-block">
                      {(selectedOrder as any).metadata?.pick_up_code || (selectedOrder as any).pickUpCode || "—"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Produits */}
              {(selectedOrder as any).items && (selectedOrder as any).items.length > 0 && (
                <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-2xl p-4 border-2 border-primary/40 shadow-md">
                  <h3 className="text-base font-black text-gray-900 mb-3 uppercase tracking-wide flex items-center gap-2">
                    <Package className="w-5 h-5 text-primary" />
                    Produits
                  </h3>
                  <div className="space-y-3">
                    {(selectedOrder as any).items.map((item: any, idx: number) => (
                      <div
                        key={idx}
                        className={`flex items-center gap-3 p-3 bg-white rounded-xl border-2 shadow-sm ${
                          item.retrait?.statut === "RETIRE_GLOVO" ||
                          item.retrait?.statut === "RETIRE" ||
                          item.retrait?.statut === "A_RETIRER_TABLETTE"
                            ? "border-red-300 opacity-60"
                            : "border-primary/30"
                        }`}
                      >
                        <div className="w-14 h-14 md:w-16 md:h-16 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100 shadow-md">
                          {(item.productImageUrl || item.imageUrl) ? (
                            <img
                              src={photoReduite(item.productImageUrl || item.imageUrl, 160)}
                              alt={item.productName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <Package className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm md:text-base font-bold text-gray-900 leading-tight">{nomArticle(item.glovoProductId || item.productId, item.productName, langue, item.nomAr)}</p>
                          {/* CHEZ QUI LE PRENDRE (13/10/2026). Un nouveau
                              responsable ne connait pas encore les
                              fournisseurs : chaque article dit le sien, celui
                              de son panier. Achete a la caisse : « Caisse ». */}
                          {(() => {
                            const panier = ((selectedOrder as any).baskets || []).find(
                              (b: any) => b.id === item.basketId && b.status !== "CANCELLED",
                            );
                            const caisse = item.achatCaisse === true || item.indisponible === true;
                            const nom = caisse ? t("Caisse") : panier?.fournisseurName || "";
                            return (
                              <p
                                className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
                                  caisse
                                    ? "bg-amber-100 text-amber-800"
                                    : nom
                                      ? "bg-primary/10 text-primary"
                                      : "bg-gray-100 text-gray-500"
                                }`}
                              >
                                <Users className="w-3 h-3" />
                                {nom || t("Fournisseur à définir")}
                              </p>
                            );
                          })()}
                          {/* PAYER PAR LA CAISSE (21/10/2026). Le responsable donne le
                              TOTAL de la ligne ; le prix a l'unite est calcule. */}
                          {(() => {
                            const q = Math.max(1, Number(item.quantity) || 1);
                            if (item.caissePayee) {
                              const tot = Number(item.caissePayee.totalCentimes) / 100;
                              const un = Number(item.caissePayee.unitCentimes) / 100;
                              return (
                                <p className="mt-1.5 inline-flex items-center gap-1 rounded-lg bg-green-100 px-2 py-1 text-xs font-bold text-green-900">
                                  <CheckCircle className="w-3 h-3" />
                                  {t("Payé par la caisse")} : {tot.toFixed(2).replace(/\.00$/, "")} DH
                                  {q > 1 ? ` (${un.toFixed(2).replace(/\.00$/, "")} DH × ${q})` : ""}
                                </p>
                              );
                            }
                            const stR = item.retrait?.statut as string | undefined;
                            const ouverte =
                              selectedOrder.status === OrderStatus.PENDING ||
                              selectedOrder.status === OrderStatus.GLOVO_ACCEPTED ||
                              selectedOrder.status === OrderStatus.IN_PREPARATION;
                            if (!ouverte || !item.id || (stR && stR !== "REFUSE")) return null;
                            if (caisseOuverte === item.id) {
                              const tot = Number(String(caisseTotal).replace(",", "."));
                              const un = Number.isFinite(tot) && tot > 0 ? tot / q : 0;
                              return (
                                <div className="mt-2 rounded-lg border-2 border-amber-300 bg-amber-50 p-2">
                                  <label className="block text-xs font-bold text-amber-900">
                                    {t("Total payé (DH)")}
                                  </label>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    autoFocus
                                    value={caisseTotal}
                                    onChange={(ev) => setCaisseTotal(ev.target.value)}
                                    placeholder="0"
                                    className="mt-1 w-32 rounded-lg border-2 border-amber-300 bg-white px-2 py-1.5 text-base font-bold tabular-nums"
                                  />
                                  {q > 1 && un > 0 && (
                                    <p className="mt-1 text-xs font-semibold text-amber-900 tabular-nums">
                                      = {(Math.round(un * 100) / 100).toFixed(2).replace(/\.00$/, "")} DH {t("l'unité")} (÷ {q})
                                    </p>
                                  )}
                                  <div className="mt-2 flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => void payerParCaisse(item)}
                                      disabled={caisseEnvoi === item.id}
                                      className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                                    >
                                      {caisseEnvoi === item.id ? "..." : t("Enregistrer")}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setCaisseOuverte(null);
                                        setCaisseTotal("");
                                      }}
                                      disabled={caisseEnvoi === item.id}
                                      className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-bold text-gray-700"
                                    >
                                      {t("Annuler")}
                                    </button>
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  setCaisseOuverte(item.id);
                                  setCaisseTotal("");
                                  setRetraitAConfirmer(null);
                                }}
                                className="mt-2 mr-2 inline-block rounded-lg border-2 border-amber-400 bg-white px-2 py-1 text-xs font-bold text-amber-800 active:scale-95"
                              >
                                {t("Payer par la caisse")}
                              </button>
                            );
                          })()}
                          {/* RETRAIT D'UN ARTICLE INDISPONIBLE (21/10/2026). */}
                          {(() => {
                            const st = item.retrait?.statut as string | undefined;
                            if (st === "EN_ATTENTE" || st === "EN_COURS") {
                              return (
                                <p className="mt-1.5 inline-flex items-center gap-1 rounded-lg bg-amber-100 px-2 py-1 text-xs font-bold text-amber-900">
                                  <Clock className="w-3 h-3" />
                                  {t("Retrait demandé — en attente du patron")}
                                </p>
                              );
                            }
                            if (st === "RETIRE_GLOVO" || st === "RETIRE") {
                              return (
                                <p className="mt-1.5 inline-flex items-center gap-1 rounded-lg bg-red-100 px-2 py-1 text-xs font-bold text-red-800">
                                  <XCircle className="w-3 h-3" />
                                  {t("Retiré de la commande — ne pas préparer")}
                                </p>
                              );
                            }
                            if (st === "A_RETIRER_TABLETTE") {
                              return (
                                <p className="mt-1.5 rounded-lg bg-orange-100 px-2 py-1 text-xs font-bold text-orange-900">
                                  {t("Retrait validé — retire-le sur la tablette Glovo")}
                                </p>
                              );
                            }
                            if (st === "REFUSE") {
                              return (
                                <p className="mt-1.5 inline-flex items-center gap-1 rounded-lg bg-blue-100 px-2 py-1 text-xs font-bold text-blue-900">
                                  <AlertCircle className="w-3 h-3" />
                                  {t("Retrait refusé — à préparer")}
                                </p>
                              );
                            }
                            const modifiable =
                              selectedOrder.status === OrderStatus.PENDING ||
                              selectedOrder.status === OrderStatus.GLOVO_ACCEPTED ||
                              selectedOrder.status === OrderStatus.IN_PREPARATION;
                            if (!modifiable || !item.id || item.caissePayee || caisseOuverte === item.id) return null;
                            if (retraitAConfirmer === item.id) {
                              return (
                                <div className="mt-2 rounded-lg border-2 border-red-300 bg-red-50 p-2">
                                  <p className="text-xs font-bold text-red-900">
                                    {t("Retirer cet article de la commande ? Il passera en ventes perdues.")}
                                  </p>
                                  <div className="mt-2 flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => void demanderRetrait(item.id)}
                                      disabled={retraitEnvoi === item.id}
                                      className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                                    >
                                      {retraitEnvoi === item.id ? "..." : t("Oui, retirer")}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setRetraitAConfirmer(null)}
                                      disabled={retraitEnvoi === item.id}
                                      className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-bold text-gray-700"
                                    >
                                      {t("Non")}
                                    </button>
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <button
                                type="button"
                                onClick={() => setRetraitAConfirmer(item.id)}
                                className="mt-2 inline-block rounded-lg border-2 border-red-300 bg-white px-2 py-1 text-xs font-bold text-red-700 active:scale-95"
                              >
                                {t("Indisponible — retirer de la commande")}
                              </button>
                            );
                          })()}
                        </div>
                        <div className="flex-shrink-0">
                          <div className="bg-primary px-4 py-2 rounded-xl shadow-lg">
                            <p className="text-xl md:text-2xl font-black text-white text-center leading-none">×{item.quantity}</p>
                          </div>
                          {/* SOUS LA QUANTITE : LA PHOTO DE L'EMBALLAGE, ET RIEN D'AUTRE
                              (21/10/2026). Le poids (« 15 kg » pour un demi-plateau
                              d'oeufs) et le nom de l'emballage ecrit en toutes
                              lettres perturbaient l'equipe : demande du patron. */}
                          {photoEmballage(item.nomEmballage) && (
                            <img
                              src={photoEmballage(item.nomEmballage) as string}
                              alt={String(item.nomEmballage || "")}
                              title={String(item.nomEmballage || "")}
                              className="mt-1 w-full h-11 object-contain rounded-lg border border-gray-200 bg-white"
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fournisseurs à récupérer (nom + panier 1/2/3) - toujours visible en préparation */}
              {((selectedOrder.status === OrderStatus.IN_PREPARATION || selectedOrder.status === OrderStatus.GLOVO_ACCEPTED) &&
                ((selectedOrder as any).baskets?.length > 0 || (selectedOrder as any).items?.length > 0)) && (
                <div className="rounded-xl bg-gray-50/80 p-4 border border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("À récupérer chez")}</h3>
                  {(selectedOrder as any).baskets && (selectedOrder as any).baskets.length > 0 ? (
                  <div className="space-y-2">
                    {(selectedOrder as any).baskets.map((basket: any) => {
                      // Le panier ne sert plus a l'affichage : seul le nom du
                      // fournisseur compte pour aller recuperer.
                      const fournLabel = basket.fournisseurName || t("Fournisseur");
                      // Le code de retrait de la COMMANDE, repete sur chaque
                      // ligne. Meme source que le grand code du haut : deux
                      // endroits qui afficheraient un code different seraient
                      // pires qu'un seul.
                      const codeRetrait =
                        (selectedOrder as any).metadata?.pick_up_code ||
                        (selectedOrder as any).pickUpCode ||
                        null;
                      return (
                        <div key={basket.id} className="border border-gray-100 rounded-lg p-3 bg-white flex justify-between items-center shadow-sm">
                          <div>
                            {/* Le code de retrait est celui de la COMMANDE : tous les
                                paniers d'une commande le partagent. Il est repete ici
                                pour que le responsable n'ait pas a remonter en haut du
                                volet a chaque fournisseur. */}
                            <span className="font-semibold text-gray-900">{fournLabel}</span>
                            {codeRetrait && (
                              <span className="ml-2 px-2 py-1 text-xs font-mono font-bold rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                                {codeRetrait}
                              </span>
                            )}
                            <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                              basket.status === "PICKED_UP" ? "bg-green-100 text-green-800" :
                              basket.status === "READY" ? "bg-blue-100 text-blue-800" :
                              "bg-yellow-100 text-yellow-800"
                            }`}>
                              {t(BASKET_STATUS_LABELS[basket.status] || basket.status)}
                            </span>
                          </div>
                          {(basket.status === "PENDING" || basket.status === "PREPARING") && (
                            <button
                              onClick={() => handleValiderPourFournisseur(basket.id)}
                              disabled={processing === basket.id}
                              className="px-3 py-1 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
                              title="Le fournisseur n'a pas le temps : je valide et je récupère à sa place"
                            >
                              <CheckCircle className="w-4 h-4" />
                              {processing === basket.id ? "..." : t("Valider à sa place")}
                            </button>
                          )}
                          {basket.status === "READY" && (
                            <button
                              onClick={() => handleBasketPickup(basket.id)}
                              disabled={processing === basket.id}
                              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                            >
                              <CheckCircle className="w-4 h-4" />
                              {processing === basket.id ? "..." : t("Récupérer")}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  ) : (
                    <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
                      Aucun panier créé : les produits ne sont pas liés à des fournisseurs (vérifiez les catégories des fournisseurs du store et le matching produits).
                    </p>
                  )}
                </div>
              )}

              {/* Actions - Section normale en bas du contenu */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-4 border-2 border-gray-100 shadow-sm">
                <div className="flex flex-wrap gap-2">
                  {selectedOrder.status === OrderStatus.PENDING && (
                    <>
                      <button
                        onClick={() => handleCancel(selectedOrder.id)}
                        disabled={processing === selectedOrder.id}
                        className="px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold text-sm flex items-center gap-2 shadow-md"
                      >
                        <XCircle className="w-5 h-5" />
                        {t("Annuler")}
                      </button>
                      <button
                        onClick={() => handleAccept(selectedOrder.id)}
                        disabled={processing === selectedOrder.id}
                        className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-black text-base flex items-center justify-center gap-2 shadow-xl"
                      >
                        <CheckCircle className="w-5 h-5" />
                        {processing === selectedOrder.id ? "..." : t("ACCEPTER LA COMMANDE")}
                      </button>
                    </>
                  )}
                  {(selectedOrder.status === OrderStatus.IN_PREPARATION ||
                    selectedOrder.status === OrderStatus.GLOVO_ACCEPTED ||
                    selectedOrder.status === OrderStatus.GLOVO_READY) && (
                    <button
                      onClick={() => handleImprimerTicket(selectedOrder)}
                      className="w-full px-6 py-3 bg-white border-2 border-gray-800 text-gray-900 rounded-xl hover:bg-gray-50 active:scale-95 transition-all font-black text-base flex items-center justify-center gap-2 shadow-sm"
                    >
                      <Printer className="w-5 h-5" />
                      {t("IMPRIMER LE TICKET")}
                    </button>
                  )}
                  {((selectedOrder.status === OrderStatus.IN_PREPARATION || selectedOrder.status === OrderStatus.GLOVO_ACCEPTED) &&
                    (selectedOrder as any).baskets?.length > 0 &&
                    (selectedOrder as any).baskets.every((b: any) => b.status === "PICKED_UP" || b.status === "CANCELLED")) && (
                    <button
                      onClick={() => handleMarkReady(selectedOrder.id)}
                      disabled={processing === selectedOrder.id}
                      className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-black text-base flex items-center justify-center gap-2 shadow-xl"
                    >
                      <CheckCircle className="w-5 h-5" />
                      {processing === selectedOrder.id ? "..." : t("MARQUER COMME PRÊTE")}
                    </button>
                  )}
                  {((selectedOrder.status === OrderStatus.IN_PREPARATION || selectedOrder.status === OrderStatus.GLOVO_ACCEPTED) &&
                    (!(selectedOrder as any).baskets || (selectedOrder as any).baskets.length === 0)) && (
                    <button
                      onClick={() => handleMarkReady(selectedOrder.id)}
                      disabled={processing === selectedOrder.id}
                      className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-black text-base flex items-center justify-center gap-2 shadow-xl"
                    >
                      <CheckCircle className="w-5 h-5" />
                      {processing === selectedOrder.id ? "..." : t("MARQUER COMME PRÊTE")}
                    </button>
                  )}
                  {selectedOrder.status === OrderStatus.GLOVO_READY && (
                    <button
                      onClick={() => handleMarkDelivered(selectedOrder.id)}
                      disabled={processing === selectedOrder.id}
                      className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-black text-base flex items-center justify-center gap-2 shadow-xl"
                    >
                      <CheckCircle className="w-5 h-5" />
                      {processing === selectedOrder.id ? "..." : t("MARQUER COMME LIVRÉE")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================================================================
          COMBIEN DE SACS SONT PARTIS

          Cet ecran s'intercale entre « livrée » et la validation. Il est
          volontairement minuscule : deux lignes, quatre gros boutons, et
          c'est fini. Hakim le fera trente fois par jour — tout ce qui prend
          plus de trois secondes finira par etre bacle ou contourne.

          Pas de clavier : sur un telephone, taper un chiffre demande
          d'ouvrir le clavier, viser, puis le refermer. Deux appuis sur « + »
          vont plus vite et ne se trompent pas de touche.

          Le bouton de validation reste GRIS tant que le total est a zero.
          Une commande part forcement dans quelque chose ; zero partout, ce
          serait un oubli, pas une information.
          =================================================================== */}
      {sacsPour && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-5 py-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" />
                {t("Emballages utilisés")}
              </h2>
              <p className="text-green-50 text-xs mt-0.5">
                {(sacsPour as any).orderCode ||
                  sacsPour.externalId ||
                  sacsPour.id.slice(0, 8)}
              </p>
            </div>

            <div className="p-5 space-y-3">
              {[
                {
                  cle: "kraft",
                  libelle: t("Sac kraft"),
                  valeur: sacKraft,
                  poser: setSacKraft,
                },
                {
                  cle: "tissu",
                  libelle: t("Sac tissu"),
                  valeur: sacTissu,
                  poser: setSacTissu,
                },
              ].map((ligne) => (
                <div
                  key={ligne.cle}
                  className="flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3"
                >
                  <span className="font-bold text-gray-800 text-base">
                    {ligne.libelle}
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      aria-label={`Retirer un ${ligne.libelle}`}
                      onClick={() => ligne.poser(Math.max(0, ligne.valeur - 1))}
                      disabled={ligne.valeur === 0}
                      className="w-11 h-11 rounded-xl bg-white border-2 border-gray-300 text-gray-700 text-2xl font-bold leading-none active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      −
                    </button>
                    <span className="w-10 text-center text-2xl font-black tabular-nums text-gray-900">
                      {ligne.valeur}
                    </span>
                    <button
                      type="button"
                      aria-label={`Ajouter un ${ligne.libelle}`}
                      onClick={() => ligne.poser(ligne.valeur + 1)}
                      className="w-11 h-11 rounded-xl bg-green-600 text-white text-2xl font-bold leading-none active:scale-95 hover:bg-green-700"
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}

              {sacKraft + sacTissu === 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {t("Indique au moins un sac pour pouvoir valider.")}
                </p>
              )}
            </div>

            <div className="px-5 pb-5 flex gap-2">
              <button
                type="button"
                onClick={() => setSacsPour(null)}
                disabled={processing === sacsPour.id}
                className="px-5 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold active:scale-95 disabled:opacity-50"
              >
                {t("Annuler")}
              </button>
              <button
                type="button"
                onClick={() => void confirmerLivraison()}
                disabled={
                  sacKraft + sacTissu === 0 || processing === sacsPour.id
                }
                className="flex-1 px-5 py-3 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white font-black text-base flex items-center justify-center gap-2 shadow-xl active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <CheckCircle className="w-5 h-5" />
                {processing === sacsPour.id ? "..." : t("VALIDER LA LIVRAISON")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
